/**
 * EventDetailsPage - Strona szczegółów wydarzenia
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getEventBySlug, deleteEvent } from '../lib/eventService';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import { EventDetails } from '../components/events';
import { Loading } from '../components/Loading';
import { ConfirmDialog } from '../components/dialogs';
import './EventDetailsPage.css';

export const EventDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = useUserRole();
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const canEdit = hasPermission('events.edit');
  const canDelete = hasPermission('events.delete');

  useEffect(() => {
    if (slug) {
      loadEvent();
    }
  }, [slug]);

  const loadEvent = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      const result = await getEventBySlug(slug);

      if (result.success && result.data) {
        setEventId(result.data.id);
        setEventTitle(result.data.title);
      } else {
        setError(result.error || 'Nie znaleziono wydarzenia');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (eventId) {
      navigate(`/wydarzenia/edytuj/${eventId}`);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!eventId) return;

    setDeleting(true);
    try {
      const result = await deleteEvent(eventId);
      if (result.success) {
        navigate('/wydarzenia');
      } else {
        alert(result.error || 'Nie udało się usunąć wydarzenia');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !eventId) {
    return (
      <div className="premium-page-bg">
        <div className="event-details-page">
          <div className="event-details-page__error">
            <h2>Błąd</h2>
            <p>{error || 'Nie znaleziono wydarzenia'}</p>
            <button onClick={() => navigate('/wydarzenia')} className="btn-primary">
              Powrót do listy wydarzeń
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{eventTitle} - Wydarzenia - wojskaspecjalne.pl</title>
        <meta name="description" content={`Szczegóły wydarzenia: ${eventTitle}`} />
      </Helmet>

      <div className="premium-page-bg">
        <div className="event-details-page">
          {/* Breadcrumbs */}
          <nav className="event-details-page__breadcrumbs">
            <a href="/wydarzenia">Wydarzenia</a>
            <span className="event-details-page__breadcrumb-separator">›</span>
            <span>{eventTitle}</span>
          </nav>

          {/* Event Details */}
          <EventDetails eventId={eventId} onEdit={handleEdit} canEdit={canEdit} />

          {/* Admin Actions */}
          {canDelete && (
            <div className="event-details-page__admin-actions">
              <button onClick={handleDeleteClick} className="btn-danger">
                Usuń Wydarzenie
              </button>
            </div>
          )}

          {/* Delete Confirmation Dialog */}
          <ConfirmDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title="Usuń Wydarzenie"
            description={`Czy na pewno chcesz usunąć wydarzenie "${eventTitle}"? Ta operacja jest nieodwracalna.`}
            confirmLabel="Usuń"
            cancelLabel="Anuluj"
            confirmVariant="destructive"
            onConfirm={handleConfirmDelete}
            loading={deleting}
          />
        </div>
      </div>
    </>
  );
};

